const GLOBAL_KEY = "_recs_event_bus";

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
export interface EventMap {}

type EventName = keyof EventMap;
type Listener<K extends EventName> = (payload: EventMap[K]) => void;

type EventBufferMap = Map<EventName, unknown[]>;

function getEventBufferMap(): EventBufferMap {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return ((globalThis as any)[GLOBAL_KEY] ||= new Map<
    EventName,
    unknown[]
  >()) as EventBufferMap;
}

export function resetGlobalBuffer() {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  delete (globalThis as any)[GLOBAL_KEY];
}

export function publish<K extends EventName>(
  type: K,
  payload: EventMap[K],
): void {
  const buffer = getEventBufferOrInit(getEventBufferMap(), type);
  buffer.push(payload);
  if (buffer.length > 100) {
    buffer.shift();
  }

  document.dispatchEvent(new CustomEvent(type, { detail: payload }));
}

function getEventBufferOrInit<K extends EventName>(
  bufferMap: EventBufferMap,
  type: K,
): EventMap[K][] {
  let buffer = bufferMap.get(type) as EventMap[K][] | undefined;
  if (!buffer) {
    buffer = [];
    bufferMap.set(type, buffer);
  }
  return buffer;
}

export function subscribe<K extends EventName>(
  type: K,
  listener: Listener<K>,
  options: { replay?: boolean } = {},
): () => void {
  const { replay = true } = options;

  if (replay) {
    const buffer = getEventBufferMap().get(type) as EventMap[K][] | undefined;
    buffer?.forEach((value) => listener(value));
  }

  const eventListener = (e: Event): void => {
    listener((e as CustomEvent<EventMap[K]>).detail);
  };
  document.addEventListener(type, eventListener);

  return () => {
    document.removeEventListener(type, eventListener);
  };
}
